import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Vue from 'vue'

@Module({
  namespaced: true
})
export default class SalesStore extends VuexModule {
  saleTermsLoaded = false
  saleTerms: KwixeoSales.SaleTerms = []
  pdfModelsLoaded = false
  pdfModels: KwixeoSales.PdfModelsHeads = []
  accountsLoaded = false
  accounts: KwixeoSales.Accounts = []
  grantsLoaded = false
  grants: KwixeoSales.Grants = []
  recoveryStepsLoaded = false
  recoverySteps: KwixeoSales.InvoiceRecoverySteps = []
  contractModelsLoaded = false
  contractModels: KwixeoSales.ContractModels = []

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['SaleTerms', 'PdfModels', 'Accounts', 'Grants', 'RecoverySteps', 'ContractModels']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })
  }

  // Sort a list alphabetically
  @Mutation
  orderList (param: { list: KwixeoInterface.GenericObject[]; key: string }): void {
    const key = param.key
    param.list.sort((a, b) => {
      const aKey = a[key].toLowerCase()
      const bKey = b[key].toLowerCase()
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }

  // Change the loaded state
  @Mutation
  setSaleTermsLoaded (state = true): void {
    this.saleTermsLoaded = state
  }

  // Set the sale terms
  @Mutation
  setSaleTerms (saleTerms: KwixeoSales.SaleTerms): void {
    this.saleTerms = saleTerms
  }

  // Add or update a sale term
  @Mutation
  updateSaleTerm (saleTerm: KwixeoSales.SaleTerm): void {
    if (!this.saleTermsLoaded) {
      return
    }
    const index = this.saleTerms.findIndex(type => type.id === saleTerm.id)
    if (index >= 0) {
      Vue.set(this.saleTerms, index, saleTerm)
    } else {
      this.saleTerms.push(saleTerm)
    }
  }

  // Remove a sale term
  @Mutation
  removeSaleTerm (saleTerm: KwixeoSales.SaleTerm): void {
    if (!this.saleTermsLoaded) {
      return
    }
    const index = this.saleTerms.findIndex(type => type.id === saleTerm.id)
    if (index >= 0) {
      Vue.delete(this.saleTerms, index)
    }
  }

  // Load the sale terms from api
  @Action
  async loadSaleTerms (force = false): Promise<void> {
    if (!this.saleTermsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/sale_term?per_page=500&sort=label')
      await this.context.commit('setSaleTerms', response.data.data)
      this.context.commit('setSaleTermsLoaded')
    }
  }

  @Action
  async loadSaleTerm (id: number): Promise<void> {
    if (!this.saleTermsLoaded) {
      return
    }
    const response = await Vue.prototype.$kwSales.axios.get('/sales/sale_term/' + id)
    await this.context.commit('updateSaleTerm', response.data)
  }

  // Add or update a sale term
  @Action
  setSaleTerm (saleTerm: KwixeoSales.SaleTerm): void {
    this.context.commit('updateSaleTerm', saleTerm)
    if (this.saleTermsLoaded) {
      this.context.commit('orderList', { list: this.saleTerms, key: 'label' })
    }
  }

  // Delete a sale term
  @Action
  deleteSaleTerm (saleTerm: KwixeoSales.SaleTerm): void {
    this.context.commit('removeSaleTerm', saleTerm)
  }


  // Change the loaded state
  @Mutation
  setPdfModelsLoaded (state = true): void {
    this.pdfModelsLoaded = state
  }

  // Set the pdf models
  @Mutation
  setPdfModels (pdfModels: KwixeoSales.PdfModelsHeads): void {
    this.pdfModels = pdfModels
  }

  // Add or update a pdf model
  @Mutation
  updatePdfModel (pdfModel: KwixeoSales.PdfModelsHead): void {
    if (!this.pdfModelsLoaded) {
      return
    }
    const index = this.pdfModels.findIndex(type => type.id === pdfModel.id)
    if (index >= 0) {
      Vue.set(this.pdfModels, index, pdfModel)
    } else {
      this.pdfModels.push(pdfModel)
    }
  }

  // Remove a pdf model
  @Mutation
  removePdfModel (pdfModel: KwixeoSales.PdfModelsHead): void {
    if (!this.pdfModelsLoaded) {
      return
    }
    const index = this.pdfModels.findIndex(type => type.id === pdfModel.id)
    if (index >= 0) {
      Vue.delete(this.pdfModels, index)
    }
  }

  // Load the pdf models from the server
  @Action
  async loadPdfModels (force = false): Promise<void> {
    if (!this.pdfModelsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/pdf_model?per_page=500&sort=label')
      await this.context.commit('setPdfModels', response.data.data)
      this.context.commit('setPdfModelsLoaded')
    }
  }

  // Add or update a pdf model
  @Action
  setPdfModel (pdfModel: KwixeoSales.PdfModelsHead|KwixeoSales.PdfModel): void {
    // Remove unnecessary properties
    Vue.delete(pdfModel, 'document')
    Vue.delete(pdfModel, 'columns')
    Vue.delete(pdfModel, 'top')
    Vue.delete(pdfModel, 'bottom')
    Vue.delete(pdfModel, 'footer')
    Vue.delete(pdfModel, 'tableOptions')
    Vue.delete(pdfModel, 'sale_term')
    Vue.delete(pdfModel, 'cover')

    this.context.commit('updatePdfModel', pdfModel)
    if (this.pdfModelsLoaded) {
      this.context.commit('orderList', { list: this.pdfModels, key: 'label' })
    }
  }

  // Delete a pdf model
  @Action
  deletePdfModel (pdfModel: KwixeoSales.PdfModelsHead): void {
    this.context.commit('removePdfModel', pdfModel)
  }

  // Return the default pdf model to use for a type
  get defaultPdfModel (): (type: string) => KwixeoSales.PdfModelsHead|undefined {
    return (type: string): KwixeoSales.PdfModelsHead|undefined => {
      const key = 'use_' + type
      let defaultModel = this.pdfModels.find((model: KwixeoInterface.GenericObject) => model[key + '_default'] === true)
      if (!defaultModel) {
        defaultModel = this.pdfModels.find((model: KwixeoInterface.GenericObject) => model[key] === true)
      }
      return defaultModel
    }
  }


  // Change the loaded state
  @Mutation
  setAccountsLoaded (state = true): void {
    this.accountsLoaded = state
  }

  // Set the accounts
  @Mutation
  setAccounts (accounts: KwixeoSales.Accounts): void {
    this.accounts = accounts
  }

  // Add or update an account
  @Mutation
  updateAccount (account: KwixeoSales.Account): void {
    if (!this.accountsLoaded) {
      return
    }
    const index = this.accounts.findIndex(type => type.id === account.id)
    if (index >= 0) {
      Vue.set(this.accounts, index, account)
    } else {
      this.accounts.push(account)
    }
  }

  // Remove an account
  @Mutation
  removeAccount (account: KwixeoSales.Account): void {
    if (!this.accountsLoaded) {
      return
    }
    const index = this.accounts.findIndex(type => type.id === account.id)
    if (index >= 0) {
      Vue.delete(this.accounts, index)
    }
  }

  // Load the accounts from the server
  @Action
  async loadAccounts (force = false): Promise<void> {
    if (!this.accountsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/account?per_page=500&sort=name&include=syncAccount,syncAccount.bank')
      await this.context.commit('setAccounts', response.data.data)
      this.context.commit('setAccountsLoaded')
    }
  }

  // Add or update a pdf model
  @Action
  setAccount (account: KwixeoSales.Account): void {
    this.context.commit('updateAccount', account)
    if (this.accountsLoaded) {
      this.context.commit('orderList', { list: this.accounts, key: 'name' })
    }
  }

  // Delete a pdf model
  @Action
  deleteAccount (account: KwixeoSales.Account): void {
    this.context.commit('removeAccount', account)
  }

  // Return the default customer type to use
  get defaultAccount (): KwixeoSales.Account|undefined {
    return this.accounts.find(account => account.use_default)
  }


  // Change the loaded state
  @Mutation
  setGrantsLoaded (state = true): void {
    this.grantsLoaded = state
  }

  // Set the grants
  @Mutation
  setGrants (grants: KwixeoSales.Grants): void {
    this.grants = grants
  }

  // Add or update a grant
  @Mutation
  updateGrant (grant: KwixeoSales.Grant): void {
    if (!this.grantsLoaded) {
      return
    }
    const index = this.grants.findIndex(type => type.id === grant.id)
    if (index >= 0) {
      Vue.set(this.grants, index, grant)
    } else {
      this.grants.push(grant)
    }
  }

  // Remove a grant
  @Mutation
  removeGrant (grant: KwixeoSales.Grant): void {
    if (!this.grantsLoaded) {
      return
    }
    const index = this.grants.findIndex(type => type.id === grant.id)
    if (index >= 0) {
      Vue.delete(this.grants, index)
    }
  }

  // Load the grants from api
  @Action
  async loadGrants (force = false): Promise<void> {
    if (!this.grantsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/grant?per_page=500&sort=label')
      await this.context.commit('setGrants', response.data.data)
      this.context.commit('setGrantsLoaded')
    }
  }

  // Add or update a grant
  @Action
  setGrant (grant: KwixeoSales.Grant): void {
    this.context.commit('updateGrant', grant)
    if (this.grantsLoaded) {
      this.context.commit('orderList', { list: this.grants, key: 'label' })
    }
  }

  // Delete a grant
  @Action
  deleteGrant (grant: KwixeoSales.Grant): void {
    this.context.commit('removeGrant', grant)
  }


  // Change the loaded state
  @Mutation
  setRecoveryStepsLoaded (state = true): void {
    this.recoveryStepsLoaded = state
  }

  // Set the recovery steps
  @Mutation
  setRecoverySteps (steps: KwixeoSales.InvoiceRecoverySteps): void {
    this.recoverySteps = steps
  }

  // Add or update a recovery step
  @Mutation
  updateRecoveryStep (step: KwixeoSales.InvoiceRecoveryStep): void {
    if (!this.recoveryStepsLoaded) {
      return
    }
    const index = this.recoverySteps.findIndex(el => el.id === step.id)
    if (index >= 0) {
      Vue.set(this.recoverySteps, index, step)
    } else {
      this.recoverySteps.push(step)
    }
  }

  // Remove a recovery step
  @Mutation
  removeRecoveryStep (step: KwixeoSales.InvoiceRecoveryStep): void {
    if (!this.recoveryStepsLoaded) {
      return
    }
    const index = this.recoverySteps.findIndex(el => el.id === step.id)
    if (index >= 0) {
      Vue.delete(this.recoverySteps, index)
    }
  }

  // Load the recovery steps from api
  @Action
  async loadRecoverySteps (force = false): Promise<void> {
    if (!this.recoveryStepsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/recovery-step?per_page=500&sort=delay')
      await this.context.commit('setRecoverySteps', response.data.data)
      this.context.commit('setRecoveryStepsLoaded')
    }
  }

  // Add or update a recovery step
  @Action
  setRecoveryStep (step: KwixeoSales.InvoiceRecoveryStep): void {
    this.context.commit('updateRecoveryStep', step)
    if (this.recoveryStepsLoaded) {
      this.context.commit('orderList', { list: this.recoverySteps, key: 'delay' })
    }
  }

  // Delete a recovery step
  @Action
  deleteRecoveryStep (step: KwixeoSales.InvoiceRecoveryStep): void {
    this.context.commit('removeRecoveryStep', step)
  }


  // Change the loaded state
  @Mutation
  setContractModelsLoaded (state = true): void {
    this.contractModelsLoaded = state
  }

  // Set the contract models
  @Mutation
  setContractModels (models: KwixeoSales.ContractModels): void {
    this.contractModels = models
  }

  // Add or update a contract model
  @Mutation
  updateContractModels (model: KwixeoSales.ContractModel): void {
    if (!this.contractModelsLoaded) {
      return
    }
    const index = this.contractModels.findIndex(el => el.id === model.id)
    if (index >= 0) {
      Vue.set(this.contractModels, index, model)
    } else {
      this.contractModels.push(model)
    }
  }

  // Remove a contract model
  @Mutation
  removeContractModel (model: KwixeoSales.ContractModel): void {
    if (!this.contractModelsLoaded) {
      return
    }
    const index = this.contractModels.findIndex(el => el.id === model.id)
    if (index >= 0) {
      Vue.delete(this.contractModels, index)
    }
  }

  // Load the contract models from api
  @Action
  async loadContractModels (force = false): Promise<void> {
    if (!this.contractModelsLoaded || force) {
      const response = await Vue.prototype.$kwSales.axios.get('/sales/contract-model', {
        params: {
          per_page: 500,
          sort: 'name',
          include: ['payment_term', 'payment_method', 'pdf_model', 'documents', 'contents']
        }
      })
      await this.context.commit('setContractModels', response.data.data)
      this.context.commit('setContractModelsLoaded')
    }
  }

  @Action
  async loadContractModel (id: number): Promise<void> {
    if (!this.contractModelsLoaded) {
      return
    }

    const response = await Vue.prototype.$kwSales.axios.get('/sales/contract-model/' + id, {
      params: {
        include: ['payment_term', 'payment_method', 'pdf_model', 'documents', 'contents']
      }
    })
    await this.context.commit('updateContractModels', response.data)
  }

  // Add or update a contract model
  @Action
  setContractModel (model: KwixeoSales.ContractModel): void {
    this.context.commit('updateContractModel', model)
    if (this.contractModelsLoaded) {
      this.context.commit('orderList', { list: this.contractModels, key: 'name' })
    }
  }

  // Delete a contract model
  @Action
  deleteContractModel (model: KwixeoSales.ContractModel): void {
    this.context.commit('removeContractModel', model)
  }
}
